import { sanitizeHtml, stripHtmlTags } from '@/lib/helpers';

/**
 * Decode HTML entities (like &nbsp;, &amp;, etc.)
 * Uses browser's native HTML entity decoding
 *
 * @param text - Text containing HTML entities
 * @returns Decoded text with entities converted to characters
 */
export function decodeHtmlEntities(text: string): string {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
}

/**
 * Format email subject for plain text display (no HTML)
 * Removes all HTML tags, decodes HTML entities, and replaces variables with plain text
 *
 * @param inputString - The raw email subject (may contain HTML and template variables)
 * @returns Plain text string with readable variable names
 */
export function formatEmailSubjectPlainText(inputString: string): string {
    if (!inputString) return '';

    // Step 1: Remove all HTML tags
    let outputString = stripHtmlTags(inputString);

    // Step 2: Decode HTML entities (like &nbsp;, &amp;, etc.)
    outputString = decodeHtmlEntities(outputString);

    // Step 3: Replace exact variable patterns with readable names
    outputString = outputString.replaceAll('{my_username}', '<span class="italic ">My Username</span>');
    outputString = outputString.replaceAll('{my_name}', '<span class="italic ">My Name</span>');
    outputString = outputString.replaceAll('{customer_name}', '<span class="italic ">Customer Name</span>');
    outputString = outputString.replaceAll('{product_name}', '<span class="italic ">Product Name</span>');

    return outputString;
}

/**
 * Format email subject with styling for variable items
 * Extracts variable items from HTML and styles them with italic text
 *
 * @param inputString - The raw email subject (may contain HTML and template variables)
 * @returns HTML string with subtly styled variable items
 */
export function formatEmailSubjectVariables(inputString: string): string {
    if (!inputString) return '';

    let outputString = inputString;

    const variableItemRegex =
        /<span[^>]*class="[^"]*variable-item[^"]*"[^>]*>.*?<div[^>]*class="[^"]*content[^"]*"[^>]*>(.*?)<\/div>.*?<\/span>/gi;
    const variableItems: { placeholder: string; name: string }[] = [];

    outputString = outputString.replace(variableItemRegex, (_, name) => {
        const placeholder = `__VARIABLE_${variableItems.length}__`;
        variableItems.push({ placeholder, name: name.trim() });
        return placeholder;
    });

    // Remove all remaining HTML tags
    outputString = stripHtmlTags(outputString);

    // Decode HTML entities (like &nbsp;, &amp;, etc.)
    outputString = decodeHtmlEntities(outputString);

    variableItems.forEach(({ placeholder, name }) => {
        outputString = outputString.replace(
            placeholder,
            `<span class="italic font-bold bg-brand-subtle text-accent-strong px-0.5 rounded-md">${name}</span>`
        );
    });

    // Replace exact variable patterns with subtle styling
    const replacePattern = (pattern: string, displayName: string) => {
        outputString = outputString.replaceAll(
            pattern,
            `<span class="italic font-bold bg-brand-subtle text-accent-strong px-0.5 rounded-md">${displayName}</span>`
        );
    };

    replacePattern('{my_username}', 'My Username');
    replacePattern('{my_name}', 'My Name');
    replacePattern('{customer_name}', 'Customer Name');
    replacePattern('{product_name}', 'Product Name');

    // Sanitize the final output to prevent XSS
    return sanitizeHtml(outputString);
}
